<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import FilterChip from './Filters/FilterChip.vue';
import useLocale from '../../composables/useLocale';
import type { Field, FilterOption, SingleField } from '../../search-panel-types';
import { useDisplay } from 'vuetify';
import type { SortOption } from '../../types';
import type { ContentType } from '@ilteducation/content-registry-types';
import { useFilters } from '../../composables/use-filters';
import useFormatFilterOptions from '../../composables/use-format-filter-options';
import useRangeLabel from '../../composables/use-range-label';
import { getRangeBounds, isOpenEndedRange } from '../../content-filters';

type Emits = {
  (e: 'change-sort', sortOption: SortOption): void;
  (e: 'back'): void;
};
const emit = defineEmits<Emits>();

type Props = {
  contentType: ContentType;
  sortOptions: SortOption[];
  filterFields: Field[];
  totalHits: number;
  isLoading: boolean;
  selectedSortOption?: string;
  backButtonLabel?: string;
};
const props = withDefaults(defineProps<Props>(), {
  totalHits: 0,
  isLoading: false,
  selectedSortOption: undefined,
  backButtonLabel: undefined,
});

const { smAndUp, smAndDown } = useDisplay();
const { t } = useLocale();
const { selectedFilters, removeSelectedFilterByValue, removeAllFilters } = useFilters();
const { transformToFilterOptions } = useFormatFilterOptions();
const { formatRangeLabel } = useRangeLabel();

// All fields that can hold a selected value, including the sub-fields of grouped fields (e.g. multiple_switches)
const singleFields = computed<SingleField[]>(() =>
  props.filterFields.flatMap((field) => ('fields' in field ? field.fields : [field])),
);

const selectedSortOption = ref<SortOption>(
  props.sortOptions.find((s) => s.key === props.selectedSortOption) ?? props.sortOptions[0],
);

const handleRemoveFilter = (filterKey: string, value: FilterOption): void => {
  removeSelectedFilterByValue(filterKey, value);
};

const handleRemoveAllFilters = () => {
  removeAllFilters();
};

/**
 * The route only holds values, so the readable label is derived from the field, e.g.
 * duration '[360 TO 16140]' → '6-269 min', languages.text 'sv' → 'Svenska'. Returns undefined while a list label
 * can't be resolved yet (e.g. categories still loading), so the chip doesn't flash the raw id.
 */
const getChipLabel = (key: string, option: FilterOption): string | undefined => {
  const field = singleFields.value.find((singleField) => singleField.aggregationKey === key);
  if (!field) {
    return option.value;
  }

  // Sub-fields of a switch group are typed 'list', so the switch is recognized by its parent group
  const isSwitch = props.filterFields.some(
    (filterField) =>
      filterField.type === 'multiple_switches' && 'fields' in filterField && filterField.fields.includes(field),
  );
  if (isSwitch) {
    return t(field.label) as string;
  }

  const isRangeField = field.type === 'range' || field.type === 'multiple_predefined_ranges';
  const bounds = getRangeBounds(option.value);
  if (isRangeField && bounds) {
    const isOpenEnded = isOpenEndedRange(field.ranges, bounds);
    return formatRangeLabel(key, bounds, field.valueLabelMappings, isOpenEnded);
  }

  // Ignore the field's kind, as find() may return the subject field for a grade id, since both share 'categories'
  const [listOption] = transformToFilterOptions({ ...field, kind: undefined }, [{ key: option.value, count: 0 }]);
  return listOption?.label;
};

watch([selectedSortOption], () => emit('change-sort', selectedSortOption.value));
</script>

<template>
  <div class="d-flex flex-wrap ga-2">
    <div
      v-if="backButtonLabel && !smAndDown"
      class="back-btn-bg btn-bg-height rounded d-flex align-center bg-surface clickable-container"
    >
      <v-btn
        class="back-btn rounded px-3 text-none"
        color="on-surface"
        variant="text"
        height="48"
        :ripple="false"
        @click="emit('back')"
      >
        <v-icon size="x-large" icon="mdi-chevron-left" class="pr-2"></v-icon>
        {{ backButtonLabel }}
      </v-btn>
    </div>
    <div class="btn-bg-height rounded d-flex align-center bg-inverse-primary clickable-container">
      <v-btn
        v-if="sortOptions.length > 0"
        class="sort-btn rounded text-none"
        color="inverse"
        variant="text"
        :prepend-icon="selectedSortOption.icon"
        :ripple="false"
      >
        {{ selectedSortOption.label }}
        <v-icon size="x-large" icon="mdi-menu-down"></v-icon>
        <v-menu activator="parent" class="test">
          <v-list class="pa-0" :aria-label="t('content_search.search.sort.none')" role="menu">
            <v-item-group v-model="selectedSortOption">
              <v-item v-for="option in sortOptions" :key="option.label" v-slot="{ isSelected, toggle }" :value="option">
                <v-list-item
                  role="menuitem"
                  tabindex="0"
                  v-if="!isSelected"
                  :active="isSelected"
                  color="primary"
                  @click="toggle"
                >
                  <template #prepend>
                    <v-icon class="opacity-100" :icon="option.icon"></v-icon>
                  </template>
                  <template #title>
                    {{ option.label }}
                  </template>
                </v-list-item>
              </v-item>
            </v-item-group>
          </v-list>
        </v-menu>
      </v-btn>
    </div>
    <div
      class="rounded pa-1 d-inline-flex bg-secondary-container align-center search-result-label px-2"
      v-if="!isLoading || totalHits > 0"
    >
      <v-icon>mdi-magnify</v-icon>
      <div>
        {{ smAndUp ? `${t('content_search.label_search_results')} (${totalHits})` : totalHits.toString() }}
      </div>
    </div>
    <template v-for="(i, key) in selectedFilters" :key="key">
      <template v-for="item in selectedFilters[key]" :key="item.value">
        <FilterChip
          v-if="getChipLabel(key as string, item)"
          :is-selected="true"
          :label="getChipLabel(key as string, item)"
          :aria-label="`${t('button_remove')} ${getChipLabel(key as string, item)}`"
          @click="handleRemoveFilter(key as string, item)"
        />
      </template>
    </template>
    <FilterChip
      v-if="Object.keys(selectedFilters).length > 0"
      :is-selected="true"
      color="bg-error-container"
      text-color="on-error-container"
      :label="t('content_search.label_remove_all_filters')"
      @click="handleRemoveAllFilters"
    />
  </div>
</template>

<style lang="scss" scoped>
.sort-btn {
  font-size: 0.875rem;
}

.back-btn {
  font-size: 0.875rem;
}

.back-btn-bg {
  border: 1px solid rgb(var(--v-theme-outline-variant));
}

.search-result-label {
  font-size: 14px;
}

/**
* Style div to look and act as a button, to make clickable area bigger while keeping the appearance of a smaller button.
 */
.btn-bg-height {
  height: 36px !important;
}

.clickable-container:hover {
  box-shadow: inset 0 0 0 36px rgba(0, 0, 0, 0.04); /* simulate hover area */
}

.clickable-container:active {
  box-shadow: inset 0 0 0 36px rgba(0, 0, 0, 0.2); /* simulate pressed area */
}

.v-btn:hover {
  --v-hover-opacity: 0;
}

.v-btn {
  --v-activated-opacity: 0;
}
</style>
